import React from 'react'
import { useTranslation } from 'react-i18next'

import clsx from 'clsx'
import { format } from 'date-fns'
import ja from 'date-fns/locale/ja'

import * as styles from './AvailabilityStrip.module.scss'

import { Availability as AvailabilityType } from '../../../hooks'
import { MaskIcon } from '../../basics'
import { Availability } from '../FullCalendar/Availability'

export type StripDay = {
  date: string // 'YYYY-MM-DD'
  availability: AvailabilityType
  isCheckIn: boolean
  // Game-day info (Fighters fixtures at ES CON FIELD), resolved by the
  // container from useGameDays. isGameDay without gameImage falls back to a
  // baseball icon, mirroring FullCalendar.
  isGameDay: boolean
  gameImage?: string
}

// 'YYYY-MM-DD' → 'M/D'。Date を直接使わず文字列から組み立て、タイムゾーン揺れを
// 避ける（JST 前提の日付文字列をそのまま表示）。
const formatMD = (date: string) => {
  const [, m, d] = date.split('-').map(Number)
  return `${m}/${d}`
}

// 曜日ラベルをロケールに合わせて返す（ScheduleTable と同じ方式）。ja は date-fns の
// ja ロケールで「日〜土」、それ以外は英語3文字大文字（SUN〜SAT）。UTC で生成して
// タイムゾーン揺れを避ける。
const formatWeekday = (date: string, language: string) => {
  const [y, m, d] = date.split('-').map(Number)
  const utc = new Date(Date.UTC(y, m - 1, d))
  return language === 'ja'
    ? format(utc, 'EEE', { locale: ja })
    : format(utc, 'E').toUpperCase()
}

export const AvailabilityStrip: React.FC<{
  days: StripDay[]
  onDayClick?: (date: string) => void
}> = ({ days, onDayClick }) => {
  const { i18n } = useTranslation()
  return (
    <div className={styles.strip}>
      {days.map((day) => {
        const md = formatMD(day.date)
        const weekday = formatWeekday(day.date, i18n.language)
        const interactiveProps = onDayClick
          ? {
              role: 'button' as const,
              tabIndex: 0,
              onClick: () => onDayClick(day.date),
              onKeyDown: (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') onDayClick(day.date)
              },
            }
          : {}
        return (
          <div
            key={day.date}
            className={clsx(styles.cell, day.isCheckIn && styles.checkIn)}
            {...interactiveProps}
          >
            <div>{md}</div>
            <div>{weekday}</div>
            <div className={styles.game}>
              {day.isGameDay &&
                (day.gameImage ? (
                  <img
                    src={day.gameImage}
                    alt="Opponent logo"
                    className={styles.teamLogo}
                  />
                ) : (
                  <MaskIcon url="/img/icon/icon-baseball-black.svg" />
                ))}
            </div>
            <Availability availability={day.availability} />
          </div>
        )
      })}
    </div>
  )
}
